// client/src/utils/tennisValidation.js
import { MATCH_FORMAT, normalizeMatchFormat } from '../constants/matchFormats'
import { getMatchFormatRules } from './matchFormatRules'

/**
 * Validates if a set score follows tennis scoring rules
 * @param {number} home - Home player's games won
 * @param {number} away - Away player's games won
 * @returns {boolean} - True if valid tennis score
 */
export function isValidSetScore(
  home,
  away,
  matchFormat = MATCH_FORMAT.TRADITIONAL,
) {
  // Convert to numbers and validate range
  const homeGames = Number(home)
  const awayGames = Number(away)

  const normalizedMatchFormat = normalizeMatchFormat(matchFormat)
  const rules = getMatchFormatRules(normalizedMatchFormat)

  if (
    Number.isNaN(homeGames) ||
    Number.isNaN(awayGames) ||
    homeGames < 0 ||
    awayGames < 0 ||
    homeGames > rules.maxGamesPerSet ||
    awayGames > rules.maxGamesPerSet
  ) {
    return false
  }

  if (normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8) {
    // 8–0 through 8–6 are straight wins; 9–8 / 8–9 represent a tiebreak played at 8–8
    return (
      (homeGames === 8 && awayGames <= 6) ||
      (awayGames === 8 && homeGames <= 6) ||
      (homeGames === 9 && awayGames === 8) ||
      (awayGames === 9 && homeGames === 8)
    )
  }

  if (homeGames === 6 && awayGames <= 4) return true
  if (awayGames === 6 && homeGames <= 4) return true
  if (homeGames === 7 && (awayGames === 5 || awayGames === 6)) return true
  if (awayGames === 7 && (homeGames === 5 || homeGames === 6)) return true

  return false
}

/**
 * Validates match sets following best-of-three rules with prioritized error messaging
 *
 * Validation Priority Order (highest to lowest):
 * 1. Set sequence gaps - Cannot skip sets (e.g., empty Set 1 with filled Set 2)
 * 2. Invalid individual set scores - Must follow tennis scoring rules
 * 3. Too many sets - Maximum of 3 sets allowed
 * 4. Insufficient sets - Minimum of 2 sets for completed (non-retired) matches
 * 5. Best-of-three completion - One player must win exactly 2 sets
 *
 * @param {Array} sets - Array of set objects with home_games and away_games
 * @param {boolean} isRetired - Whether the match ended in retirement
 * @returns {Object} - Validation result with success, prioritized errors, and details
 */
export function validateMatchSets(
  sets,
  isRetired = false,
  matchFormat = MATCH_FORMAT.TRADITIONAL,
) {
  const result = {
    success: true,
    errors: [],
    warnings: [],
    setWins: { home: 0, away: 0 },
    prioritizedError: null, // The most important error to show first
  }

  // Priority 1: Check for gaps in set sequence (before filtering)
  // Cannot skip sets - must complete Set 1 before Set 2, etc.
  // Track the last non-empty set and detect any gaps in the sequence
  let lastNonEmptyIndex = -1
  for (let i = 0; i < sets.length; i++) {
    const isEmpty = sets[i].home_games === 0 && sets[i].away_games === 0

    if (!isEmpty) {
      // Found a non-empty set - check if we skipped any sets
      if (lastNonEmptyIndex === -1 && i > 0) {
        // First non-empty set is not Set 1 - there's a gap from the start
        result.success = false
        result.prioritizedError = {
          type: 'set_gap',
          message: `Set 1 must be completed before Set ${i + 1}`,
          guidance:
            'Enter scores for all sets in order, or remove later set scores.',
        }
        result.errors.push(result.prioritizedError.message)
        return result
      } else if (lastNonEmptyIndex !== -1 && i > lastNonEmptyIndex + 1) {
        // There's a gap between lastNonEmptyIndex and i
        result.success = false
        result.prioritizedError = {
          type: 'set_gap',
          message: `Set ${lastNonEmptyIndex + 2} must be completed before Set ${
            i + 1
          }`,
          guidance:
            'Enter scores for all sets in order, or remove later set scores.',
        }
        result.errors.push(result.prioritizedError.message)
        return result
      }
      lastNonEmptyIndex = i
    }
  }

  // Filter out empty sets (0-0)
  const filteredSets = sets.filter(
    (set) => set.home_games > 0 || set.away_games > 0,
  )

  const normalizedMatchFormat = normalizeMatchFormat(matchFormat)
  const { minimumSetsForNonRetiredMatch, maximumSets, setsNeededToWin } =
    getMatchFormatRules(normalizedMatchFormat)

  // Priority 2: Individual set score validation
  const invalidSetErrors = []
  filteredSets.forEach((set, index) => {
    if (
      !isValidSetScore(set.home_games, set.away_games, normalizedMatchFormat)
    ) {
      invalidSetErrors.push({
        priority: 1,
        message: `Set ${index + 1}: Invalid tennis score (${set.home_games}-${
          set.away_games
        })`,
        guidance:
          normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
            ? 'Valid pro-set scores: 8-0 through 8-6, or 9-8 (tiebreak)'
            : 'Valid scores: 6-0 through 6-4, 7-5, or 7-6',
        setIndex: index,
      })
    } else {
      // Count set wins for valid sets
      if (set.home_games > set.away_games) {
        result.setWins.home++
      } else if (set.away_games > set.home_games) {
        result.setWins.away++
      }
    }
  })

  // If there are invalid set scores, prioritize the first one
  if (invalidSetErrors.length > 0) {
    result.success = false
    result.prioritizedError = {
      type: 'invalid_set_score',
      message: invalidSetErrors[0].message,
      guidance: invalidSetErrors[0].guidance,
      setIndex: invalidSetErrors[0].setIndex,
    }
    // Add all invalid set errors to the errors array for completeness
    result.errors = invalidSetErrors.map(
      (err) => `${err.message}. ${err.guidance}`,
    )
    return result // Return early - fix set scores first
  }

  // Priority 3: Set count validation
  if (filteredSets.length > maximumSets) {
    result.success = false
    result.prioritizedError = {
      type: 'too_many_sets',
      message: 'Too many sets entered',
      guidance:
        'Tennis matches can have a maximum of 3 sets. Please remove extra sets.',
    }
    result.errors.push('Maximum of 3 sets allowed in tennis matches')
    return result
  }

  // Priority 4: Minimum sets validation (for completed matches)
  if (!isRetired && filteredSets.length < minimumSetsForNonRetiredMatch) {
    result.success = false
    result.prioritizedError = {
      type: 'insufficient_sets',
      message: 'More sets needed to complete the match',
      guidance:
        normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
          ? 'Enter one completed set to determine a winner, or select a retirement option.'
          : 'Enter scores for at least 2 sets to determine a winner, or select a retirement option.',
    }
    result.errors.push(
      normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
        ? 'At least 1 set must be played for completed matches'
        : 'At least 2 sets must be played for completed matches',
    )
    return result
  }

  // Priority 5: Best-of-three completion validation (for non-retired matches)
  if (!isRetired && filteredSets.length >= 2) {
    const homeWins = result.setWins.home
    const awayWins = result.setWins.away

    if (homeWins !== setsNeededToWin && awayWins !== setsNeededToWin) {
      result.success = false

      if (
        normalizedMatchFormat === MATCH_FORMAT.TRADITIONAL &&
        homeWins === 1 &&
        awayWins === 1
      ) {
        result.prioritizedError = {
          type: 'needs_decisive_set',
          message: 'Match is tied 1-1 sets',
          guidance:
            'Enter the score for the deciding third set to determine the winner.',
        }
      } else if (
        normalizedMatchFormat === MATCH_FORMAT.TRADITIONAL &&
        filteredSets.length === 2
      ) {
        result.prioritizedError = {
          type: 'incomplete_match',
          message: `Match incomplete (${homeWins}-${awayWins} sets)`,
          guidance:
            normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
              ? 'One player must win the pro set. Enter a valid completed set score.'
              : 'One player must win 2 sets. Enter another set score or select a retirement option.',
        }
      } else {
        result.prioritizedError = {
          type: 'invalid_match_result',
          message: `Invalid match result (${homeWins}-${awayWins} sets)`,
          guidance:
            normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
              ? 'One player must win exactly 1 set in a pro set match.'
              : 'One player must win exactly 2 sets in a best-of-three match.',
        }
      }

      result.errors.push(
        (normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8
          ? 'One player must win 1 set in a pro set match. '
          : 'One player must win 2 sets in a best-of-three match. ') +
          `Current: ${homeWins}-${awayWins} sets`,
      )
      return result
    }

    // Check for unnecessary third set
    // If after the first two valid sets one player had already won 2–0,
    // a third set was not needed.
    if (filteredSets.length === 3) {
      const firstTwo = filteredSets.slice(0, 2)
      let homeAfterTwo = 0
      let awayAfterTwo = 0
      firstTwo.forEach((set) => {
        if (set.home_games > set.away_games) homeAfterTwo++
        else if (set.away_games > set.home_games) awayAfterTwo++
      })
      if (homeAfterTwo === 2 || awayAfterTwo === 2) {
        result.warnings.push('Third set not needed - match was already won 2-0')
      }
    }
  }

  return result
}

/**
 * Get user-friendly error messages for common invalid scores
 * @param {number} home - Home player's games
 * @param {number} away - Away player's games
 * @returns {string} - Helpful error message
 */
export function getScoreErrorMessage(
  home,
  away,
  matchFormat = MATCH_FORMAT.TRADITIONAL,
) {
  const normalizedMatchFormat = normalizeMatchFormat(matchFormat)

  if (home < 0 || away < 0) {
    return 'Scores cannot be negative'
  }

  const maxGames = normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8 ? 9 : 7

  if (home > maxGames || away > maxGames) {
    return `Maximum ${maxGames} games per set`
  }

  if (normalizedMatchFormat === MATCH_FORMAT.PRO_SET_8) {
    if (home === away) {
      return `${home}-${away} is not a valid pro-set score - one player must win by 2`
    }

    if (Math.abs(home - away) === 1 && Math.max(home, away) >= 7) {
      return `${home}-${away} is not a valid pro-set score - need a 2-game margin at 8+ games`
    }

    if (Math.max(home, away) < 8) {
      return `${home}-${away} is incomplete - play to 8 games minimum (win by 2)`
    }

    return `${home}-${away} is not a valid pro-set score. Valid scores are 8-0 through 8-6, or 9-8 (tiebreak)`
  }

  if (home === away && home >= 5) {
    return `${home}-${away} is not a valid tennis score - one player must win by 2`
  }

  if (Math.abs(home - away) === 1 && Math.max(home, away) >= 6) {
    return `${home}-${away} is not a valid tennis score - need 2-game margin or tiebreak`
  }

  const higher = Math.max(home, away)

  if (higher === 5) {
    return `${home}-${away} is incomplete - play to 6 games (or 7 if opponent reaches 5)`
  }

  if (higher < 6) {
    return `${home}-${away} is incomplete - play to 6 games minimum`
  }

  return `${home}-${away} is not a valid tennis score`
}

/**
 * Suggests what score ranges are valid for current state
 * @param {number} currentScore - Current games for one player
 * @param {number} opponentScore - Opponent's current games
 * @returns {Array} - Array of valid score options for current player
 */
export function getValidScoreOptions(currentScore, opponentScore) {
  const validOptions = []

  for (let score = 0; score <= 7; score++) {
    if (isValidSetScore(score, opponentScore)) {
      validOptions.push(score)
    }
  }

  return validOptions
}
